import React, { useRef, useState, useEffect } from 'react';
import { Drawer } from 'vaul';
import { X, ChevronRight, AlertCircle, Loader2 } from 'lucide-react';
import type { Swiper as SwiperType } from 'swiper';
import { getExchangeCredentialCopy } from '@/utils/exchange-config';
import { useExchangeSetup } from '@/hooks/useExchangeSetup';
import { toast } from 'sonner';
import { getTradeErrorMessage } from '@/utils/trade-error';
import { ExchangeTutorialContent } from './ExchangeTutorialContent';
import { ExchangeHighlight } from './ExchangeHighlight';

interface ExchangeSetupDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  exchangeCode: string;
  exchangeName: string;
  userId: string;
}

type TabType = 'tutorial' | 'credentials';

export const ExchangeSetupDrawer: React.FC<ExchangeSetupDrawerProps> = ({
  open,
  onOpenChange,
  exchangeCode,
  exchangeName,
  userId,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('tutorial');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [apiPassphrase, setApiPassphrase] = useState('');
  const [setupError, setSetupError] = useState<string | null>(null);
  const [copiedIp, setCopiedIp] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);

  const credentials = getExchangeCredentialCopy(exchangeCode);

  const apiKeyFieldRef = useRef<HTMLInputElement>(null);
  const apiSecretFieldRef = useRef<HTMLInputElement>(null);
  const apiPassphraseFieldRef =  useRef<HTMLInputElement>(null);
  const drawerContentRef = useRef<HTMLDivElement>(null);

  // Config with the IP to allowlist, and a submit for this exchange
  const { config, submit, isPending } = useExchangeSetup(exchangeCode, userId);

  // Handle iOS keyboard dismissal and viewport resize
  useEffect(() => {
    if (!open) return;

    const handleResize = () => {
      // Force reflow to restore drawer height after keyboard closes
      // Use a small delay to ensure viewport has updated
      setTimeout(() => {
        if (drawerContentRef.current) {
          const drawer = drawerContentRef.current;
          const drawerContent = drawer.parentElement as HTMLElement;
          if (drawerContent) {
            // Reset any inline height styles to let CSS take over
            drawerContent.style.height = '';
            // Force a reflow by accessing offsetHeight
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            drawerContent.offsetHeight;
            // Ensure drawer content is restored to full height
            const viewportHeight = window.visualViewport?.height || window.innerHeight;
            if (viewportHeight > 0) {
              drawerContent.style.height = `${Math.min(viewportHeight * 0.95, window.innerHeight * 0.95)}px`;
            }
          }
        }
      }, 100);
    };

    // Listen for viewport resize (iOS keyboard open/close)
    window.addEventListener('resize', handleResize);
    // Listen for visual viewport changes (more reliable on iOS)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
    };
  }, [open]);

  // Restore slide position when switching back to tutorial tab
  useEffect(() => {
    if (activeTab === 'tutorial' && swiperInstance && currentSlideIndex > 0) {
      swiperInstance.slideTo(currentSlideIndex, 0);
    }
  }, [activeTab, swiperInstance, currentSlideIndex]);

  // Dynamically adjust arrow position based on image height
  useEffect(() => {
    if (!open || activeTab !== 'tutorial' || !imageContainerRef.current) return;

    const adjustArrowPosition = () => {
      const imageContainer = imageContainerRef.current;
      if (!imageContainer) return;

      const img = imageContainer.querySelector('img');
      if (!img) return;

      // Get actual rendered image height
      const imgHeight = img.offsetHeight;
      const containerHeight = imageContainer.offsetHeight;
      
      // Calculate the center position of the image within the container
      // Image is centered with max-h-[400px], so we need to find its actual position
      const topOffset = (containerHeight - imgHeight) / 2;
      const arrowPosition = topOffset + (imgHeight / 2);
      
      // Update CSS custom property for arrow positioning
      const swiperElement = imageContainer.closest('.tutorial-swiper');
      if (swiperElement instanceof HTMLElement) {
        swiperElement.style.setProperty('--arrow-top', `${arrowPosition}px`);
      }
    };

    // Adjust on mount and when images load
    const timer = setTimeout(adjustArrowPosition, 100);
    window.addEventListener('resize', adjustArrowPosition);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', adjustArrowPosition);
    };
  }, [open, activeTab, currentSlideIndex]);

  const handleCopyIp = async () => {
    if (config?.whitelistIp) {
      await navigator.clipboard.writeText(config.whitelistIp);
      setCopiedIp(true);
      toast.success('IP address copied to clipboard');
      setTimeout(() => setCopiedIp(false), 2000);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Blur all inputs to close keyboard on iOS
    apiKeyFieldRef.current?.blur();
    apiSecretFieldRef.current?.blur();
    apiPassphraseFieldRef.current?.blur();
    
    // Trigger drawer height restoration after keyboard closes
    setTimeout(() => {
      if (drawerContentRef.current) {
        const drawerContent = drawerContentRef.current.parentElement as HTMLElement;
        if (drawerContent) {
          const viewportHeight = window.visualViewport?.height || window.innerHeight;
          if (viewportHeight > 0) {
            drawerContent.style.height = `${Math.min(viewportHeight * 0.9, window.innerHeight * 0.9)}px`;
          }
        }
      }
    }, 150);
    
    if (!apiKey.trim()) {
      // Small delay before focusing to ensure keyboard closed
      setTimeout(() => apiKeyFieldRef.current?.focus(), 200);
      toast.error(credentials.keyEmpty);
      return;
    }

    if (!apiSecret.trim()) {
      setTimeout(() => apiSecretFieldRef.current?.focus(), 200);
      toast.error(credentials.secretEmpty);
      return;
    }
    
    if (config?.requiresPassphrase && !apiPassphrase.trim()) {
      setTimeout(() => apiPassphraseFieldRef.current?.focus(), 200);
      toast.error(credentials.passphraseEmpty);
      return;
    }

    setSetupError(null);

    try {
      await submit(apiKey.trim(), apiSecret.trim(), apiPassphrase.trim());
      
      toast.success(`${exchangeName} connected successfully!`);
      
      // Reset form and close drawer
      setApiKey('');
      setApiSecret('');
      setApiPassphrase('');
      setSetupError(null);
      setActiveTab('tutorial');
      setCurrentSlideIndex(0);
      setSwiperInstance(null);
      onOpenChange(false);
    } catch (error) {
      const errorMessage = getTradeErrorMessage(error, 'Failed to setup exchange');
      setSetupError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    setApiKey('');
    setApiSecret('');
    setApiPassphrase('');
    setSetupError(null);
    setActiveTab('tutorial');
    setCurrentSlideIndex(0);
    setSwiperInstance(null);
    onOpenChange(false);
  };

  if (!config) {
    return null;
  }

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange} repositionInputs={false} dismissible={false}>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 bg-black/80 z-[100]" />
        <Drawer.Content className="bg-secondary flex flex-col fixed bottom-0 left-0 right-0 max-h-[95vh] rounded-t-[10px] z-[101]" style={{ height: '95vh' }}>
          <div ref={drawerContentRef} className="flex flex-col h-full min-h-0">
          {/* Header */}
          <div className="flex-shrink-0 border-b border-border">
            <div className="flex items-center justify-between p-4">
              <h2 className="text-xl font-bold text-text-primary">
                Connect {exchangeName}
              </h2>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-primary rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-text-secondary" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-border">
              <button
                onClick={() => setActiveTab('tutorial')}
                className={`flex-1 px-4 py-3 font-medium transition-colors ${
                  activeTab === 'tutorial'
                    ? 'text-accent border-b-2 border-accent'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                Tutorial
              </button>
              <button
                onClick={() => setActiveTab('credentials')}
                className={`flex-1 px-4 py-3 font-medium transition-colors ${
                  activeTab === 'credentials'
                    ? 'text-accent border-b-2 border-accent'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                API Credentials
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {activeTab === 'tutorial' ? (
              <div className="pb-20">
                <ExchangeTutorialContent
                  config={config}
                  exchangeCode={exchangeCode}
                  exchangeName={exchangeName}
                  copiedIp={copiedIp}
                  currentSlideIndex={currentSlideIndex}
                  onCopyIp={handleCopyIp}
                  onSwiperInit={setSwiperInstance}
                  onSlideChange={(swiper) => setCurrentSlideIndex(swiper.activeIndex)}
                  imageContainerRef={imageContainerRef}
                  maxImageHeight="400px"
                />
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6 pb-20">
                {config.highlight && <ExchangeHighlight highlight={config.highlight} compact />}
                {/* API Key Input */}
                <div className="space-y-2">
                  <label htmlFor="api-key-input" className="block text-sm font-medium text-text-primary">
                    {credentials.keyLabel}
                  </label>
                  <input
                    ref={apiKeyFieldRef}
                    id="api-key-input"
                    type="text"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder={credentials.keyPlaceholder}
                    className="w-full bg-primary border border-border rounded-lg px-4 py-3 text-text-primary placeholder-text-secondary focus:outline-none focus:border-accent transition-colors"
                    disabled={isPending}
                  />
                </div>

                {/* API Secret Input */}
                <div className="space-y-2">
                  <label htmlFor="api-secret-input" className="block text-sm font-medium text-text-primary">
                    {credentials.secretLabel}
                  </label>
                  <input
                    ref={apiSecretFieldRef}
                    id="api-secret-input"
                    type="password"
                    value={apiSecret}
                    onChange={(e) => setApiSecret(e.target.value)}
                    placeholder={credentials.secretPlaceholder}
                    className="w-full bg-primary border border-border rounded-lg px-4 py-3 text-text-primary placeholder-text-secondary focus:outline-none focus:border-accent transition-colors"
                    disabled={isPending}
                  />
                  {credentials.showSecretNote && (
                    <p className="text-xs text-text-secondary">
                      Note: The API Secret is different from your API Key
                    </p>
                  )}
                  {credentials.secretHint && (
                    <p className="text-xs text-text-secondary">
                      {credentials.secretHint}
                    </p>
                  )}
                </div>

                {config.requiresPassphrase && (
                  <div className="space-y-2">
                    <label htmlFor="api-passphrase-input" className="block text-sm font-medium text-text-primary">
                      {credentials.passphraseLabel}
                    </label>
                    <input
                      ref={apiPassphraseFieldRef}
                      id="api-passphrase-input"
                      type={credentials.maskPassphrase ? "password" : "text"}
                      value={apiPassphrase}
                      onChange={(e) => setApiPassphrase(e.target.value)}
                      placeholder={credentials.passphrasePlaceholder}
                      className="w-full bg-primary border border-border rounded-lg px-4 py-3 text-text-primary placeholder-text-secondary focus:outline-none focus:border-accent transition-colors"
                      disabled={isPending}
                    />
                    {credentials.passphraseHint && (
                      <p className="text-xs text-text-secondary">
                        {credentials.passphraseHint}
                      </p>
                    )}
                  </div>
                )}

                {/* Error Display */}
                {setupError && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-red-500 mb-1">
                          Setup Failed
                        </h4>
                        <p className="text-sm text-text-secondary">{setupError}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isPending}
                  className="w-full bg-accent hover:bg-accent-hover text-text-primary py-3 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isPending ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    'Connect Exchange'
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Sticky Continue Button */}
          {activeTab === 'tutorial' && (
            <div className="flex-shrink-0 border-t border-border bg-secondary p-4">
              <button
                onClick={() => setActiveTab('credentials')}
                className="w-full bg-accent hover:bg-accent-hover text-text-primary py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                Continue to API Setup
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
};

